import { getContract, zeroAddress } from "viem";
// GLOBAL_GUARD_ABI is a local human-readable signature array (not in @aastar/core);
// parseAbi is required to feed it to viem's getContract during the ethers->viem migration.
// eslint-disable-next-line no-restricted-imports
import { parseAbi } from "viem";
import { EthereumProvider, type ViemContract } from "../providers/ethereum-provider";
import {
  readAccountTierLimits,
  readAccountGuardAddress,
  readGuardDailyAllowance,
  readAlgorithmApproved,
} from "../providers/typed-reads";
import { GLOBAL_GUARD_ABI } from "../constants/entrypoint";
import {
  TierConfig,
  GuardStatus,
  PreCheckResult,
  ALG_ECDSA,
  ALG_P256,
  ALG_BLS,
  ALG_CUMULATIVE_T2,
  ALG_CUMULATIVE_T3,
  ALG_CUMULATIVE_T2_WA,
  ALG_CUMULATIVE_T3_WA,
} from "../../core/tier";
import { resolveTier, algIdForTier } from "../../core/tier";
import { ILogger, ConsoleLogger } from "../interfaces/logger";

const ALG_NAMES: Record<number, string> = {
  [ALG_BLS]: "BLS (0x01)",
  [ALG_ECDSA]: "ECDSA (0x02)",
  [ALG_P256]: "P256 (0x03)",
  [ALG_CUMULATIVE_T2]: "Cumulative T2 (0x04)",
  [ALG_CUMULATIVE_T3]: "Cumulative T3 (0x05)",
  [ALG_CUMULATIVE_T2_WA]: "Cumulative T2 WebAuthn (0x09)",
  [ALG_CUMULATIVE_T3_WA]: "Cumulative T3 WebAuthn (0x0a)",
};

/**
 * Pre-checks transactions against GlobalGuard before submitting on-chain.
 * Avoids wasted gas from predictable reverts.
 */
export class GuardChecker {
  private readonly logger: ILogger;

  constructor(
    private readonly ethereum: EthereumProvider,
    logger?: ILogger
  ) {
    this.logger = logger ?? new ConsoleLogger("[GuardChecker]");
  }

  /**
   * Fetch tier limits from an AirAccount contract.
   */
  async fetchTierConfig(accountAddress: string): Promise<TierConfig> {
    const account = this.ethereum.getAccountContract(accountAddress);
    // Typed wrapper returns both uint256 tier limits as bigint.
    return readAccountTierLimits(account);
  }

  /**
   * Fetch guard status from the account's GlobalGuard.
   */
  async fetchGuardStatus(accountAddress: string): Promise<GuardStatus> {
    const account = this.ethereum.getAccountContract(accountAddress);

    // getConfigDescription returns a single tuple struct; the typed wrapper reads
    // the `guardAddress` field that decides whether the guard is enforced at all.
    const guardAddress = await readAccountGuardAddress(account);

    if (guardAddress === zeroAddress) {
      return {
        hasGuard: false,
        guardAddress: zeroAddress,
        dailyLimit: 0n,
        dailyRemaining: 0n,
      };
    }

    const guard = getContract({
      address: guardAddress,
      abi: parseAbi(GLOBAL_GUARD_ABI),
      client: this.ethereum.getProvider(),
    }) as unknown as ViemContract;
    // Typed wrapper returns both uint256 daily-allowance values as bigint.
    const { dailyLimit, dailyRemaining } = await readGuardDailyAllowance(guard);

    return {
      hasGuard: true,
      guardAddress,
      dailyLimit,
      dailyRemaining,
    };
  }

  /**
   * Pre-check a transaction: determine tier, check guard limits and algorithm approval.
   * Returns errors array (empty = OK to proceed).
   */
  async preCheck(accountAddress: string, value: bigint, useWebAuthnPasskey = false): Promise<PreCheckResult> {
    const errors: string[] = [];

    // Fetch tier config → resolve tier → get algId. The device-passkey (WebAuthn) path signs the WA
    // cumulative variant (0x09/0x0a), which is what such accounts approve; querying the raw 0x04/0x05
    // gave a false "not approved" and blocked Tier-3 device-passkey transfers (#256).
    const tierConfig = await this.fetchTierConfig(accountAddress);
    const tier = resolveTier(value, tierConfig);
    const algId = algIdForTier(tier, useWebAuthnPasskey);

    // Fetch guard status
    const guard = await this.fetchGuardStatus(accountAddress);

    if (!guard.hasGuard) {
      return { ok: true, errors: [], tier, algId };
    }

    // Check daily allowance
    if (guard.dailyLimit > 0n && value > guard.dailyRemaining) {
      errors.push(
        `Daily limit exceeded: requesting ${value} wei but only ${guard.dailyRemaining} remaining (limit: ${guard.dailyLimit})`
      );
    }

    // Check algorithm approval. v0.17.2-beta.4: the algorithm whitelist is the single
    // source of truth on the ACCOUNT (enforced in validateUserOp), not the guard.
    const accountContract = this.ethereum.getAccountContract(accountAddress);
    const isApproved = await readAlgorithmApproved(accountContract, algId);

    if (!isApproved) {
      errors.push(
        `Algorithm ${ALG_NAMES[algId] ?? `0x${algId.toString(16)}`} is not approved by the account`
      );
    }

    if (errors.length > 0) {
      this.logger.warn(`Pre-check failed for ${accountAddress}: ${errors.join("; ")}`);
    }

    return { ok: errors.length === 0, errors, tier, algId };
  }
}
